/**
 * `InstallDetector` — ADR-0025 §5. Polls `GET /v1/sites/:id/install-status`
 * every 3s up to 30 attempts (90s budget) and fires `onConnected` once
 * the first beacon lands, so the composing step can auto-verify.
 *
 * Renders NOTHING while polling or after the budget runs out (ADR-0212:
 * a spinning "waiting for the first ping" row read as a failure before
 * the merchant had pasted anything, and "no ping yet" read as an error;
 * Verify above it is the real gate). On the first ping it renders one
 * line — INSTALL DETECTED · Verifying… — and the proof status row
 * below carries the outcome.
 *
 * Polling is cancelled on unmount so a fast navigation does not leak a
 * fetch loop. The detector pulls the active site from the SiteProvider
 * so a TopBar switch swaps targets without remount.
 */

import { CheckCircle2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { useApi } from '../../api/api-context.js';
import { useSites } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";

export const INSTALL_POLL_INTERVAL_MS = 3_000;
export const INSTALL_POLL_MAX_ATTEMPTS = 30;

type DetectorState = 'polling' | 'connected' | 'timed_out';

export interface InstallDetectorProps {
  /** Called once when the detector first transitions to `connected`. */
  onConnected?: () => void;
}

export function InstallDetector({ onConnected }: InstallDetectorProps): JSX.Element | null {
  const C = useC();
  const api = useApi();
  const { activeSite } = useSites();
  const [state, setState] = useState<DetectorState>('polling');
  const attempts_ref = useRef(0);
  const stopped_ref = useRef(false);
  const onConnectedRef = useRef<typeof onConnected>(onConnected);
  // Keep the ref current so the polling closure always calls the latest
  // callback without restarting the timer.
  onConnectedRef.current = onConnected;

  useEffect(() => {
    // Reset detector state when the target site changes (TopBar switch).
    attempts_ref.current = 0;
    stopped_ref.current = false;
    setState('polling');

    if (activeSite === null) {
      return undefined;
    }
    const site_id = activeSite.site_id;

    const tick = async (): Promise<void> => {
      if (stopped_ref.current) return;
      attempts_ref.current += 1;
      try {
        const status = await api.sites.getInstallStatus(site_id);
        if (stopped_ref.current) return;
        if (status.installed) {
          setState('connected');
          stopped_ref.current = true;
          onConnectedRef.current?.();
          return;
        }
      } catch {
        // Treat transient errors as a missed poll; the next attempt
        // tries again.
      }
      if (attempts_ref.current >= INSTALL_POLL_MAX_ATTEMPTS) {
        setState('timed_out');
        stopped_ref.current = true;
      }
    };

    void tick();
    const handle = setInterval(() => {
      void tick();
    }, INSTALL_POLL_INTERVAL_MS);

    return () => {
      stopped_ref.current = true;
      clearInterval(handle);
    };
  }, [activeSite, api]);

  if (state !== 'connected') return null;

  return (
    <div
      className="flex items-center gap-2"
      style={{ fontFamily: monoFont, fontSize: 11, letterSpacing: 1.5, color: C.limeFg }}
      role="status"
      aria-live="polite"
      data-testid="install-detected"
    >
      <CheckCircle2 size={14} color={C.limeFg} />
      INSTALL DETECTED · VERIFYING…
    </div>
  );
}
